import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { content, images } from "@/i18n/content";
import { EASE, Reveal, SectionHeading } from "@/components/Reveal";
import { avifSrc, isLocalPhoto, webpSrc } from "@/lib/media";

const Priorities = () => {
  const { pick } = useLanguage();
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="priorities" data-testid="priorities-section" className="bg-[#F7F5F0] pb-24 md:pb-32 lg:pb-40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-24">
        <SectionHeading
          eyebrow={pick(content.priorities.eyebrow)}
          heading={pick(content.priorities.heading)}
          sub={pick(content.priorities.sub)}
        />

        <div data-testid="priorities-accordion" className="border-b border-[#171717]/20">
          {content.priorities.items.map((item, i) => {
            const open = openIdx === i;
            return (
              <Reveal key={item.num} delay={i * 0.05} y={30}>
                <div className="border-t border-[#171717]/20">
                  <button
                    type="button"
                    data-testid={`priority-row-${item.num}`}
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    aria-expanded={open}
                    className="group grid w-full grid-cols-[auto_1fr_auto] items-center gap-5 md:gap-10 py-7 md:py-9 text-left"
                  >
                    <span
                      className={`font-display num-row leading-none transition-colors duration-500 ${
                        open ? "text-[#C42021]" : "text-[#171717]/25 group-hover:text-[#171717]/60"
                      }`}
                    >
                      {item.num}
                    </span>
                    <span className="font-display text-2xl md:text-4xl lg:text-5xl tracking-[-0.02em] text-[#171717] leading-tight">
                      {pick(item.title)}
                    </span>
                    <motion.span
                      animate={{ rotate: open ? 45 : 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className={`flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full border transition-colors duration-500 ${
                        open
                          ? "border-[#C42021] bg-[#C42021] text-[#F7F5F0]"
                          : "border-[#171717]/30 text-[#171717] group-hover:border-[#171717]"
                      }`}
                    >
                      <Plus size={18} />
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        key="panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.6, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 pb-10 md:pb-14 md:pl-[calc(4rem+2.5rem)]">
                          <div className="overflow-hidden">
                            <picture>
                              {isLocalPhoto(images[item.image]) && (
                                <>
                                  <source srcSet={avifSrc(images[item.image])} type="image/avif" />
                                  <source srcSet={webpSrc(images[item.image])} type="image/webp" />
                                </>
                              )}
                              <motion.img
                                src={images[item.image]}
                                alt={pick(item.title)}
                                loading="lazy"
                                initial={{ scale: 1.08 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 1.1, ease: EASE }}
                                className="aspect-[4/3] w-full object-cover"
                              />
                            </picture>
                          </div>
                          <div className="flex flex-col justify-center">
                            <p className="text-base md:text-lg leading-relaxed text-[#171717]/80 max-w-xl">
                              {pick(item.desc)}
                            </p>
                            <span
                              data-testid={`priority-metric-${item.num}`}
                              className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-[#171717] px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#F7F5F0]"
                            >
                              <span className="h-1.5 w-1.5 rounded-full bg-[#C42021]" aria-hidden="true" />
                              {pick(item.metric)}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Priorities;
